import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from './context.service';
import { map, Observable, of, switchMap } from 'rxjs';
import {
  Octopus,
  WorkflowInstance,
  WorkflowInstanceSearch,
  CanStartWorkflowResult,
} from '@maximizer/core/shared/domain';
import { WorkflowStageTemplateService } from './workflow-stage-template.service';
import { WorkflowTaskTemplateService } from './workflow-task-template.service';

@Injectable()
export class WorkflowInstanceService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
    private readonly workflowStageTemplateService: WorkflowStageTemplateService,
    private readonly workflowTaskTemplateService: WorkflowTaskTemplateService,
  ) {}

  getQuery(
    params?: WorkflowInstanceSearch,
  ):
    | Octopus.SearchQuery<Octopus.WorkflowInstance>
    | Octopus.LogicalQuery<Octopus.WorkflowInstance>
    | undefined {
    if (!params) {
      return undefined;
    }
    const searchArray = [];
    if (params.key) {
      searchArray.push({
        Key: {
          $EQ: params.key,
        },
      });
    }
    if (params.workflowKey) {
      searchArray.push({
        WorkflowKey: {
          $EQ: params.workflowKey,
        },
      });
    }
    if (params.workflowStageKey) {
      searchArray.push({
        WorkflowStageKey: {
          $EQ: params.workflowStageKey,
        },
      });
    }
    if (params.workflowTaskTemplateKey) {
      searchArray.push({
        WorkflowTaskTemplateKey: {
          $EQ: params.workflowTaskTemplateKey,
        },
      });
    }

    if (Array.isArray(params.parentKey)) {
      searchArray.push({
        ParentKey: {
          $IN: params.parentKey,
        },
      });
    } else if (params.parentKey) {
      searchArray.push({
        ParentKey: {
          $EQ: params.parentKey,
        },
      });
    }

    if (Array.isArray(params.withKey)) {
      searchArray.push({
        WithKey: {
          $IN: params.withKey,
        },
      });
    } else if (params.withKey) {
      searchArray.push({
        WithKey: {
          $EQ: params.withKey,
        },
      });
    }

    if (params.completed !== undefined) {
      searchArray.push({
        Completed: {
          $EQ: params.completed,
        },
      });
    }
    if (params.dateRangeFilter) {
      const { fieldName, dateRange } = params.dateRangeFilter;
      const condition = Array.isArray(dateRange)
        ? { $RANGE: dateRange }
        : { $EQ: dateRange };
      searchArray.push({
        [fieldName]: condition,
      });
    }
    if (searchArray.length === 1) {
      return searchArray[0];
    } else if (searchArray.length > 1) {
      return {
        $AND: searchArray,
      };
    }

    return {};
  }

  read(params?: WorkflowInstanceSearch): Observable<WorkflowInstance[]> {
    const searchQuery = this.getQuery(params) ?? {};
    const request: Octopus.WorkflowInstanceReadRequest = {
      WorkflowInstance: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowKey: 1,
            WorkflowStageKey: 1,
            WorkflowTaskTemplateKey: 1,
            ParentKey: 1,
            Completed: 1,
            CreationDate: { Value: 1, DisplayValue: 1 },
            LastModifyDate: { Value: 1, DisplayValue: 1 },
            WithKey: 1,
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowInstanceReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowInstanceMapper();
            return mapper.from(result.WorkflowInstance.Data);
          }
          return [];
        }),
      );
  }

  readWithCompleted(
    activeParams?: WorkflowInstanceSearch,
    completedParams?: WorkflowInstanceSearch,
  ): Observable<WorkflowInstance[]> {
    let searchQuery = this.getQuery(activeParams);
    const completedSearchQuery = this.getQuery(completedParams);
    if (searchQuery && completedSearchQuery) {
      searchQuery = {
        $OR: [searchQuery, completedSearchQuery],
      };
    } else if (completedSearchQuery) {
      searchQuery = completedSearchQuery;
    }

    const request: Octopus.WorkflowInstanceReadRequest = {
      WorkflowInstance: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowKey: 1,
            WorkflowStageKey: 1,
            WorkflowTaskTemplateKey: 1,
            ParentKey: 1,
            WithKey: 1,
            Completed: 1,
            CreationDate: { Value: 1, DisplayValue: 1 },
            LastModifyDate: { Value: 1, DisplayValue: 1 },
          },
        },
        Criteria: {
          SearchQuery: searchQuery ?? {},
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowInstanceReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowInstanceMapper();
            return mapper.from(result.WorkflowInstance.Data);
          }
          return [];
        }),
      );
  }

  isWorkflowStarted(workflowKey: string): Observable<boolean> {
    return this.read({ workflowKey: workflowKey, completed: false }).pipe(
      map((workflowInstances) => workflowInstances.length > 0),
    );
  }

  hasTasks(workflowKey: string, hasStages: boolean): Observable<boolean> {
    if (hasStages) {
      return this.workflowStageTemplateService.hasTasksInEachStage(workflowKey);
    }
    return this.workflowTaskTemplateService.read({ workflowKey }).pipe(
      map((result) => {
        if (Array.isArray(result)) return result.length > 0;
        return result != null;
      }),
    );
  }

  // Opportunity and Customer Service entries live in WithKey; for Address Book and
  // Leads the selected entry is the anchor itself and lives in ParentKey.
  canStartWorkflow(
    keysValue: string[],
    workflowKey?: string | null,
    hasStages = false,
    matchByWithKey = false,
  ): Observable<CanStartWorkflowResult> {
    if (!workflowKey) {
      return of({
        canStart: false,
        workflowInstances: [],
        hasStages: false,
      });
    }

    return this.read({
      workflowKey,
      ...(matchByWithKey ? { withKey: keysValue } : { parentKey: keysValue }),
      completed: false,
    }).pipe(
      switchMap((workflowInstances): Observable<CanStartWorkflowResult> => {
        const instances = workflowInstances ?? [];
        return this.hasTasks(workflowKey, hasStages).pipe(
          map((canStart) => ({
            canStart,
            workflowInstances: instances,
            hasStages,
          })),
        );
      }),
    );
  }
}
